import React from 'react';
import { CTASection } from '../../components/CTASection';
import { Film, CheckCircle, Sparkles, Zap, DollarSign, Award, Layers } from 'lucide-react';
export function DirectToFilm() {
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1270" />,
    title: 'Vibrant, Lasting Colors',
    description: 'Latest DTF technology delivers bold, precise prints that endure'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-1271" />,
    title: 'No Minimums, Fast Turnaround',
    description: 'Order any quantity with speedy production times'
  }, {
    icon: <DollarSign className="w-6 h-6" data-id="element-1272" />,
    title: 'Transparent, Competitive Pricing',
    description: 'No hidden fees—just exceptional value for your budget'
  }, {
    icon: <Layers className="w-6 h-6" data-id="element-1273" />,
    title: 'Works on Any Fabric',
    description: 'Cotton, polyester, blends—DTF transfers stick to it all'
  }];
  const applications = ['Sports team uniforms', 'Family reunion shirts', 'Custom hoodies & sweatshirts', 'Hats & caps', 'Bags & totes', 'Unique gifts & keepsakes', 'Event merchandise', 'Small batch custom orders'];
  const advantages = [{
    title: 'Full-Color Capability',
    description: 'Print complex designs with unlimited colors, gradients, and photo-quality detail'
  }, {
    title: 'Soft, Flexible Feel',
    description: 'DTF transfers are lightweight and breathable—no stiff, heavy prints'
  }, {
    title: 'Durable & Wash-Resistant',
    description: 'Designs withstand repeated washing without cracking or fading'
  }, {
    title: 'Perfect for Dark Fabrics',
    description: 'Bright, opaque prints that pop on any color garment'
  }];
  return <div className="flex flex-col w-full" data-id="element-1274">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1275">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1276">
          <div className="flex justify-center mb-6" data-id="element-1277">
            <Film className="w-16 h-16 text-white" data-id="element-1278" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1279">
            Direct-to-Film (DTF) Printing
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1280">
            Unleash your imagination with stunning custom transfers. Vibrant
            colors, precise details, and incredible versatility—perfect for any
            fabric, any project.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1281">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1282">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1283">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1284">
              Custom Transfers for All Your Creative Needs
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1285">
              <p data-id="element-1286">
                Ready to take your designs to the next level? With
                Direct-to-Film (DTF) printing, you can turn your artistic
                visions into stunning, colorful transfers that truly stand out.
                Whether it's for sports teams, family reunions, or unique gifts,
                DTF delivers vibrant, durable prints on virtually any
                fabric—cotton, polyester, blends, and more.
              </p>
              <p data-id="element-1287">
                At Wear Haus Designs, we make creativity accessible with{' '}
                <span className="font-bold text-wh-pink" data-id="element-1288">
                  no minimum orders, fast turnaround times, and transparent
                  pricing
                </span>
                . From hoodies to hats, let your imagination soar with our
                cutting-edge DTF printing technology!
              </p>
            </div>
          </div>

          {/* Work Gallery */}
          <div className="mb-16" data-id="element-1289">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1290">
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1291">
                <img src="https://cdn.magicpatterns.com/uploads/c5U2yX9jh6WCzmEc5uR4cQ/DTF-1.png" alt="DTF printing example - TC Energy safety vest" className="w-full h-64 object-cover" data-id="element-1292" />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1293">
                <img src="https://cdn.magicpatterns.com/uploads/rwr3z9N2y5PfLK3WDGb1Xd/DTF2.png" alt="DTF printing example - The Space athletic wear" className="w-full h-64 object-cover" data-id="element-1294" />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1295">
                <img src="https://cdn.magicpatterns.com/uploads/4bygUf6xUNwjb21xqjdNvS/DTF-3.png" alt="DTF printing example - Baugo Fire Department logo" className="w-full h-64 object-cover" data-id="element-1296" />
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1297">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1298">
                <div className="flex items-start gap-4" data-id="element-1299">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1300">
                    {benefit.icon}
                  </div>
                  <div data-id="element-1301">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1302">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1303">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* DTF Advantages */}
          <div className="mb-16" data-id="element-1304">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1305">
              Why Choose Direct-to-Film?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1306">
              {advantages.map((advantage, index) => <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1307">
                  <h3 className="text-xl font-bold text-wh-teal mb-3" data-id="element-1308">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-700" data-id="element-1309">{advantage.description}</p>
                </div>)}
            </div>
          </div>

          {/* Applications */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1310">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1311">Perfect For</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-1312">
              {applications.map((application, index) => <div key={index} className="flex items-center gap-3" data-id="element-1313">
                  <CheckCircle className="w-6 h-6 text-wh-yellow flex-shrink-0" data-id="element-1314" />
                  <span className="text-lg" data-id="element-1315">{application}</span>
                </div>)}
            </div>
          </div>

          {/* Quality Guarantee */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 mb-16" data-id="element-1316">
            <div className="max-w-3xl mx-auto text-center text-white" data-id="element-1317">
              <Award className="w-16 h-16 mx-auto mb-6" data-id="element-1318" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1319">
                Exceptional Print Quality Guaranteed
              </h2>
              <p className="text-xl text-white/90 mb-6" data-id="element-1320">
                Using the latest DTF technology, we ensure your designs are
                printed with vibrant colors and precise details that last. Our
                Direct-to-Film method guarantees your creations will make a
                lasting impression—wash after wash, wear after wear.
              </p>
              <p className="text-lg text-white/90" data-id="element-1321">
                It's a creative dream come true with competitive rates and no
                hidden charges!
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-wh-yellow" data-id="element-1322">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1323">
              Get Started on Your Design Adventure Today!
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1324">
              Your destination for Direct-to-Film printing. From hoodies to
              hats, let your creativity soar!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1325">
              <a href="/Contact" className="inline-flex items-center justify-center px-8 py-4 bg-wh-pink text-white font-bold rounded-lg hover:bg-pink-600 transition-colors" data-id="element-1326">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1327">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1328" />
    </div>;
}